<script>
import {
  allDocumentVersions,
} from '@comp/project/queries'
import InfoTooltip from '@comp/utils/InfoTooltip.vue'
import { useIndexStore } from '@src/store/index.js'
import { parseMail } from '@src/utils/parseMail'
import { stringCannotBeEmptyRule } from '@src/utils/userInputValidation'
import gql from 'graphql-tag'
import moment from 'moment'
import MsgReader from 'msgreader'
import { createDocumentVersion } from './queries'

export default {
  name: 'NewDocVersionPopup',
  components: {
    InfoTooltip,
  },
  props: {
    projectId: {
      type: Number,
      required: true,
    },
    indexMetadata: {
      type: Array,
      required: true,
    },
    metadata: {
      type: Array,
      required: true,
    },
  },
  emits: [
    'closeAddDialog',
    'search',
  ],
  data () {
    return {
      file: null,
      loading: false,
      saveButtonClicked: false,
      titleResearching: false,
      codificationResearching: false,
      documentVersionList: [],
      documentVersion: null,
      fileReader: null,
      originGedUrl: '',
      mail: null,
      parsedData: {},
      metadataValues: {},
      selectedDate: '',
      selectedTime: '',
      requiredRules: [
        v => !!v || this.$gettext('Required field'),
      ],
      showDialog: true,
    }
  },
  computed: {
    noFileSelected () {
      return this.file === null && this.saveButtonClicked
    },
    store () {
      return useIndexStore()
    },
    disableFutureDates () {
      return [
        v => !!v || this.$gettext('Date is required'),
        v => {
          const maxDate = new Date().toISOString().substr(0, 10)
          const selectedDate = new Date(v).toISOString().substr(0, 10)
          return selectedDate <= maxDate || this.$gettext('Date cannot be in the future')
        },
      ]
    },
  },
  watch: {
    items: {
      handler() {
        this.itemsData = this.items
      },
      immediate: true,
    },
  },
  async mounted () {
    await this.setData()
    this.fileReader = new FileReader()
    this.fileReader.onload = (evt) => {
      const buffer = evt.target.result
      const msgReader = new MsgReader(buffer)
      const fileData = msgReader.getFileData()
      if (!fileData.error) {
        parseMail(fileData).then(response => {
          this.originGedUrl = response.originGedUrl
          this.parsedData = response.metadata
          this.selectedDate = response.workflowStartDatetime
          this.mail = null
        }).catch(error => {
          this.store.changeNotification({
            type: 'error',
            text: this.$gettext(error),
            autoClose: false,
          })
          this.mail = null
        })
      } else {
        this.store.changeNotification({
          type: 'error',
          text: fileData.error,
          autoClose: false,
        })
        this.mail = null
      }
    }
  },
  methods: {
    informativeMessage (metadatumName) {
      const metadatum = this.metadata.find(o => o.name === metadatumName)
      const formattingRule = metadatum?.stringFormat
      return formattingRule ? `${this.$gettext('This field has a formatting rule:')} ${formattingRule}` : null
    },
    validateAll (metadatumName) {
      const value = this.metadataValues[metadatumName]
      const metadatum = this.metadata.find(o => o.name === metadatumName)
      if (!stringCannotBeEmptyRule(value ?? '')) {
        return this.$gettext('Required field')
      }
      if (metadatum && (metadatum.stringFormat || metadatum.isChoices)) {
        const isValid = this.constructRegex(metadatumName).test(value)
        if (!isValid) {
          const formattingRule = metadatum.stringFormat
          return formattingRule
            ? `${this.$gettext('This field has a formatting rule:')} ${formattingRule}`
            : this.$gettext('Invalid value')
        }
      }
      return true
    },
    addInputFile (e) {
      this.file = e.target.files[0]
    },
    async setData () {
      if (this.$refs.form) {
        this.$refs.form.reset()
        const DATE_FORMAT = 'YYYY-MM-DD'
        this.selectedDate = `${moment(new Date()).format(DATE_FORMAT)}`
        const TIME_FORMAT = 'HH:mm'
        this.selectedTime = `${moment(new Date()).format(TIME_FORMAT)}:00`
      }
    },
    removeFile () {
      this.file = null
    },
    addDropFile (e, key) {
      this[key] = e.dataTransfer.files[0]
    },
    constructRegex (metadatumName) {
      const metadatum = this.metadata.filter(o => o.name === metadatumName)[0]
      let input
      let regex = ''
      if (metadatum.isChoices) {
        input = metadatum.choices
        regex += '('
        for (const choice of input) {
          const protectedChoice = choice.replace(/[|\\{}()[\]^$+*?.]/g, '\\$&').replace(/-/g, '\\x2d')
          regex += '|' + protectedChoice
        }
        regex += ')'
      } else {
        input = metadatum.stringFormat
        const toRegex = {
          'A': '[A-Za-z]',
          '0': '[0-9]',
          '*': '[A-ZaA-z0-9]',
          '[': '(',
          ']': ')?',
        }
        for (const char of input) {
          regex += toRegex[char]
        }
        if (!regex.length) {
          regex = '.*'
        }
      }
      return new RegExp('^' + regex + '$')
    },
    close () {
      this.$emit('closeAddDialog')
      this.removeFile()
      this.saveButtonClicked = false
    },
    parseMail (file) {
      if (file && this.fileReader.readyState !== 1) {
        this.fileReader.readAsArrayBuffer(file)
      }
    },
    formatWorkflowDateTime () {
      const datetime = `${this.selectedDate} ${this.selectedTime}`
      return moment(datetime, 'YYYY-MM-DD HH:mm').format()
    },
    addVersion () {
      if (!this.isFormValid()) {
        this.saveButtonClicked = true
        this.store.changeNotification({
          type: 'warning',
          text: this.$gettext('Please select a file and fill in all the data before submitting the document'),
          autoClose: false,
        })
      } else {
        this.loading = true
        const tmpMetadata = []
        for (const metadatum of Object.keys(this.metadataValues)) {
          tmpMetadata.push({
            name: metadatum,
            value: this.metadataValues[metadatum],
          })
        }
        return this.$graphqlQuery(createDocumentVersion, {
          contractId: this.documentVersion?.document.contract.id,
          metadataValues: tmpMetadata,
          file: this.file,
          workflowStartDatetime: this.formatWorkflowDateTime(),
          originGedUrl: this.documentVersion?.originGedUrl ?? '',
        }).then(_ => {
          this.store.changeNotification({
            type: 'positive',
            text: this.$gettext('New document version saved'),
            autoClose: true,
          })
          this.close()
          this.loading = false
        }).catch(error => {
          this.store.changeNotification({
            type: 'error',
            text: error,
            autoClose: false,
          })
          this.$emit('closeAddDialog')
          this.loading = false
        })
      }
    },
    isFormValid () {
      if (!this.file) {
        return false
      }
      for (const indexMetadatum of this.indexMetadata) {
        if (this.validateAll(indexMetadatum.name) !== true) {
          return false
        }
      }
      return true
    },
    manageResearchBar(key, value) {
      if (key === 'codification') {
        this.codificationResearching = value
      } else {
        this.titleResearching = value
      }
    },
    async onSearch(value, key) {
      if (value && value.length > 2) {
        this.manageResearchBar(key, true)
        try {
          const cleanValue = value.trim().replace(/"/g, '\\"')
          const fieldFilter = key === 'codification'
            ? `metadataValues: { inList: ["${cleanValue}"] }`
            : `${key}: { iContains: "${cleanValue}" }`
          const searchFilter = `filters: {
            document: {
              project: { id: { exact: ${this.projectId} } }
            }
            onlyLatestVersion: true
            ${fieldFilter}
          }`
          const rawQuery = allDocumentVersions
            .replace('##REPLACE##', `(\n${searchFilter}\n)`)
            .replace('##REPLACE_COUNT##', '')
          const response = await this.$graphqlQuery(gql`${rawQuery}`)
          this.manageResearchBar(key, false)
          if (response?.allDocumentVersions) {
            this.documentVersionList = response.allDocumentVersions
          }
        } catch (e) {
          this.store.changeNotification({
            type: 'error',
            text: e,
            autoClose: false,
          })
        }
      } else {
        this.documentVersionList = []
      }
    },
    onUpdateModelValue (value) {
      this.metadataValues = {}
      if (value) {
        value.metadataValues.forEach(metadatum => {
          if (metadatum.metadata?.name) {
            this.metadataValues[metadatum.metadata.name] = metadatum.value
          }
        })
      }
    },
  },
}
</script>
<template>
  <v-dialog
    v-model="showDialog"
    persistent
    scrollable
    width="40%"
  >
    <v-card>
      <v-card-title class="dialog-header">
        {{ $gettext('Add a new document version') }}
      </v-card-title>
      <v-card-text v-show="!loading">
        <v-form ref="form">
          <div>
            {{ $gettext('Document') }}
            <InfoTooltip>
              {{ $gettext('Only files in PDF format will be viewable') }}
            </InfoTooltip>
          </div>
          <div
            v-cloak
            @drop.prevent="addDropFile($event, 'file')"
            @dragover.prevent
          >
            <span
              v-if="!file"
              class="dropzone"
              :class="{ 'not-uploaded-file': noFileSelected }"
            >
              <v-icon
                color="mediumgrey"
                icon="fas fa-file-arrow-down"
              />
              {{ $gettext('Drag and drop the document here') }}
              <input
                ref="fileToAdd"
                type="file"
                hidden
                @change="addInputFile"
              >
              <v-btn
                size="small"
                color="primary"
                @click="$refs.fileToAdd.click()"
              >
                {{ $gettext('Browse file') }}
              </v-btn>
            </span>
            <span
              v-else
              class="row filename-display"
            >
              <v-icon
                icon="fas fa-file-pdf"
                color="secondary"
              />
              {{ file.name }}
              <v-icon
                color="secondary"
                size="x-small"
                icon="fas fa-times-circle"
                @click="removeFile"
              />
            </span>
            <span v-if="noFileSelected">
              {{ $gettext('Required field') }}
            </span>
          </div>
          <div class="row">
            <v-text-field
              v-model="selectedDate"
              :label="$gettext('Date received')"
              :rules="disableFutureDates"
              type="date"
            />
            <v-text-field
              v-model="selectedTime"
              :label="$gettext('Time received')"
              type="time"
            />
          </div>
        </v-form>
        <v-autocomplete
          v-model="documentVersion"
          :label="$gettext('Document title')"
          :items="documentVersionList"
          :loading="titleResearching"
          item-title="title"
          item-value="id"
          hide-no-data
          return-object
          clearable
          @update:search="(value) => onSearch(value, 'title')"
          @update:model-value="onUpdateModelValue"
        >
          <template #loader>
            <v-progress-linear
              v-if="titleResearching"
              color="primary"
              indeterminate
            />
          </template>
        </v-autocomplete>
        <v-autocomplete
          v-model="documentVersion"
          :label="$gettext('Codification')"
          :items="documentVersionList"
          :loading="codificationResearching"
          item-title="codification"
          item-value="id"
          hide-no-data
          return-object
          clearable
          @update:search="(value) => onSearch(value, 'codification')"
          @update:model-value="onUpdateModelValue"
        >
          <template #loader>
            <v-progress-linear
              v-if="codificationResearching"
              color="primary"
              indeterminate
            />
          </template>
        </v-autocomplete>
        <template
          v-for="indexMetadatum in indexMetadata"
          :key="indexMetadatum.name"
        >
          <v-select
            v-if="metadata.find(o => o.name === indexMetadatum.name)?.isChoices"
            v-model="metadataValues[indexMetadatum.name]"
            :items="metadata.find(o => o.name === indexMetadatum.name).choices"
            :label="indexMetadatum.name"
            color="secondary"
            :rules="requiredRules"
          />
          <v-text-field
            v-else
            v-model="metadataValues[indexMetadatum.name]"
            color="secondary"
            :rules="[validateAll(indexMetadatum.name)]"
            :hint="informativeMessage(indexMetadatum.name)"
            :label="indexMetadatum.name"
          />
        </template>
      </v-card-text>
      <v-card-text v-show="loading">
        <div class="row circular-loading">
          <v-progress-circular
            :width="10"
            :size="100"
            color="primary"
            indeterminate
          />
        </div>
      </v-card-text>
      <v-card-actions
        v-if="!loading"
        class="justify-end"
      >
        <v-btn
          variant="elevated"
          color="secondary"
          prepend-icon="fas fa-times"
          @click="close"
        >
          {{ $gettext('Cancel') }}
        </v-btn>
        <v-btn
          variant="elevated"
          color="primary"
          prepend-icon="fas fa-save"
          @click="addVersion"
        >
          {{ $gettext('Save') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<style lang="scss" scoped>
:deep(.v-input--horizontal .v-input__append) { // stylelint-disable-line selector-class-pattern
  margin-inline-start: unset;
}
.dropzone {
  border: 4px dashed $grey-light;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem;
  margin-bottom: 1rem;
}
.dropzone:hover {
  border-color: $primary;
}
.not-uploaded-file {
  border-color: $error-red;
}
.filename-display {
  gap: 1ch;
  background: $primary;
  color: $secondary;
  font-weight: 500;
  border-radius: 5px;
  padding: 14px;
  min-height: 10px;
  align-items: center;
  margin-bottom: 22px;
}
.circular-loading {
  justify-content: center;
  margin: 20px 0;
}
</style>
